import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

import { STORAGE_KEYS } from '@constants/common';

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

export interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
	skipOrgHeader?: boolean;
}

export const client = axios.create({ baseURL: SERVER_URL });

export const clientWithHttpResponse = axios.create({ baseURL: SERVER_URL });

export const blobClient = axios.create({
	baseURL: SERVER_URL,
	responseType: 'blob',
});

const authHandler = async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
	const token = sessionStorage.getItem('token');

	if (token) config.headers.set('Authorization', `Bearer ${token}`);

	return config;
};

const activeOrganizationIdHandler = async (config: CustomAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
	const organizationId = sessionStorage.getItem(STORAGE_KEYS.ORGANIZATION_ID);
	const platformOwnerAdministrator = sessionStorage.getItem(STORAGE_KEYS.PLATFORM_OWNER_ADMINISTRATOR) === 'true';

	if (config?.skipOrgHeader) {
		return config;
	}

	if (!platformOwnerAdministrator && organizationId) {
		config.headers.set('X-Active-Organization-Id', organizationId);
	}

	return config;
};

const respHandler = (res: AxiosResponse) => res.data;

const handlerWithFullHttpResponse = (res: AxiosResponse) => res;

const respErrorHandler = async (error: AxiosError) => {
	if (error?.response?.status === 401) {
		sessionStorage.clear();
	}

	const originalErrorMessage = error?.message;
	const errorReason = error?.response?.data
		? { ...(error.response.data as object), originalErrorMessage }
		: { originalErrorMessage };

	return Promise.reject(errorReason);
};

client.interceptors.request.use(authHandler);
client.interceptors.request.use(activeOrganizationIdHandler);
client.interceptors.response.use(respHandler, respErrorHandler);

blobClient.interceptors.request.use(authHandler);
blobClient.interceptors.request.use(activeOrganizationIdHandler);

clientWithHttpResponse.interceptors.request.use(authHandler);
clientWithHttpResponse.interceptors.request.use(activeOrganizationIdHandler);
clientWithHttpResponse.interceptors.response.use(handlerWithFullHttpResponse, respErrorHandler);
